import { ApplicationElement, html } from '../application_element/element.js';
import translations from './translations.js';
import style from './style.lit.css';
import Cropper from 'cropperjs';
import Snack from '@effectivastudio/snackbar';
import { loadImageAsDataUrl, getAspectRatio } from '../../lib/helpers.js';
import CommonActions from '../common_actions/element.js';
import { classMap } from 'lit/directives/class-map.js';

import '../icon/element.js';
import '../grid_list/element.js';

import { modalController } from '@ionic/core';
import { defineCustomElement as defineIonModal } from '@ionic/core/components/ion-modal.js';
import { defineCustomElement as defineIonHeader } from '@ionic/core/components/ion-header.js';
import { defineCustomElement as defineIonToolbar } from '@ionic/core/components/ion-toolbar.js';
import { defineCustomElement as defineIonButtons } from '@ionic/core/components/ion-buttons.js';
import { defineCustomElement as defineIonButton } from '@ionic/core/components/ion-button.js';
import { initialize } from '@ionic/core/components/index.js';

initialize();
defineIonModal();
defineIonHeader();
defineIonToolbar();
defineIonButtons();
defineIonButton();

Cropper.setDefaults({
  dragMode: 'none',
  viewMode: 1,
});

/**
 * Container with image preview and file input which triggers crop functionality within modal.
 *   - automatically sets container width depending on aspect ratio. Height is defined with ```--container-height``` CSS variable
 *   - has image preview if picture is present or successfully saved
 *   - accepts only image file types
 *   - has limit file size
 *
 * On image file drop/select:
 *   - displays image preview of chosen file with active crop functionality
 *   - has submit button for saving image on the server
 *   - has cancel button for closing modal
 *
 * If container image is present or successfully saved:
 *   - has replace button for replacing image
 *   - has delete button for deleting image from the server
 *
 * @prop imgSubmitUrl - Url for image POST request
 * @prop imgDeleteUrl - Url for image DELETE request
 * @prop imgRecommendedWidth - Recommended width of cropped image
 * @prop imgRecommendedHeight - Recommended height of cropped image
 * @prop croppedImgSrc - Url to the saved image
 * @prop limitSize - Limit size of chosen picture in KB
 * @prop imgSrc - Image Data Url of selected image => INTERNAL
 *
 * @cssprop [--container-height] Container preview height (150px)
 */

export default class UploadContainer extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  /** @internal */
  static translations = translations;

  /** @internal */
  static config = {
    icons: {
      label: 'image',
      rotate: 'redo',
    },
  };

  static properties = {
    imgSubmitUrl: { type: String },
    imgDeleteUrl: { type: String },
    imgRecommendedWidth: { type: Number },
    imgRecommendedHeight: { type: Number },
    croppedImgSrc: { type: String },
    limitSize: { type: Number },
    imgSrc: { state: true },
    dragOver: { state: true },
    imgContainerWidth: { state: true },
  };

  constructor() {
    super();

    this.commonAction = new CommonActions(this);
    this.limitSize = 10240;
  }

  disconnectedCallback() {
    this.modal?.remove();
    super.disconnectedCallback();
  }

  async firstUpdated() {
    await this.updateComplete;

    if (this.getImgContainerWidth) {
      this.imgContainerWidth = this.getImgContainerWidth;
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('imgSrc') && this.imgSrc) {
      this.handleModal();
    }
  }

  /** @internal */
  get config() {
    return this.constructor.config;
  }

  /** @internal */
  get isFileOverLimit() {
    return this.file.size > this.limitSize * 1024;
  }

  /** @internal */
  get canvasOptions() {
    return {
      canvas: true,
      maxWidth: 1024, // iOS devices limit memory => resize the image first (preferably below 1024 pixels)
      maxHeight: 1024, // iOS devices limit memory => resize the image first (preferably below 1024 pixels)
    };
  }

  /** @internal */
  get input() {
    return this.shadowRoot.querySelector('input[type="file"]');
  }

  /** @internal */
  get modalImage() {
    return this.modal.querySelector('img');
  }

  /** @internal */
  get imageDataURL() {
    const croppedCanvas = this.cropper.getCroppedCanvas({
      width: this.imgRecommendedWidth * 2,
    });

    return croppedCanvas.toDataURL('image/png', 0.9);
  }

  /** @internal */
  get fileName() {
    return `${this.file.name.split('.')[0]}.png`;
  }

  /** @internal */
  get imgAspectRatio() {
    return getAspectRatio(this.imgRecommendedWidth, this.imgRecommendedHeight);
  }

  /** @internal */
  get recommendedSizeStr() {
    return `${this.imgRecommendedWidth} X ${this.imgRecommendedHeight} px`;
  }

  /** @internal */
  get getImgContainerWidth() {
    const height = this.offsetHeight;

    return height * (this.imgAspectRatio[0] / this.imgAspectRatio[1]);
  }

  snackDanger(msg) {
    Snack({
      text: msg ? msg : this.t('snack.generic_error_msg'),
      customClass: 'snackbar-danger',
    });
  }

  async cropperPrepareImg() {
    try {
      const dataUrl = await loadImageAsDataUrl(this.file, this.canvasOptions);
      this.imgSrc = dataUrl;
    } catch (error) {
      this.snackDanger(this.t('import_error'));
      console.error(error); /* Send to Sentry */
    }
  }

  cropperInit() {
    this.cropper = new Cropper(this.modalImage, {
      aspectRatio: this.imgAspectRatio[0] / this.imgAspectRatio[1],
    });
  }

  cropperRotate() {
    this.cropper.rotate(90);
  }

  handleInputChange({ file = false }) {
    this.file = file || this.input.files[0];

    /* Reset input value and imgSrc so that user can select the same image again */
    this.input.value = null;
    this.imgSrc = null;

    /* limit file upload to the limitSize value */
    if (this.isFileOverLimit) {
      this.snackDanger(
        this.t('snack.exceeds_the_limit_msg', { limit: this.limitSize })
      );
      return;
    }

    this.cropperPrepareImg();
  }

  handleInputDragOver() {
    this.dragOver = true;
  }

  handleDragLeave() {
    this.dragOver = false;
  }

  handleInputDrop(e) {
    e.preventDefault();
    this.dragOver = false;

    const file = e.dataTransfer.items[0]; // If dropped multi files take only the first one.

    if (file.kind !== 'file') return;

    this.handleInputChange({ file: file.getAsFile() });
  }

  async handleModal() {
    const cancelBtn = document.createElement('ion-button');
    cancelBtn.innerHTML = this.t('actions.cancel');
    cancelBtn.addEventListener('click', this.modalDismiss.bind(this));

    const cropBtn = document.createElement('ion-button');
    cropBtn.innerHTML = this.t('actions.crop');
    cropBtn.addEventListener('click', this.submitImg.bind(this));

    const rotateBtn = document.createElement('button');
    rotateBtn.classList.add('btn-rotate');
    rotateBtn.innerHTML = `<ef-icon name=${this.config.icons.rotate}></ef-icon>`;
    rotateBtn.addEventListener('click', this.cropperRotate.bind(this));

    const div = document.createElement('div');

    div.innerHTML = `
      <ion-header>
        <ion-toolbar>
          <ion-buttons slot="start"></ion-buttons>
          <ion-buttons slot="end"></ion-buttons>
        </ion-toolbar>
      </ion-header>
      <div class="modal-content">
        <figure>
          <img src=${this.imgSrc} />
        </figure>
      </div>
    `;

    div.querySelector('[slot="start"]').appendChild(cancelBtn);
    div.querySelector('[slot="end"]').appendChild(cropBtn);
    div.querySelector('.modal-content figure').appendChild(rotateBtn);

    this.modal = await modalController.create({
      component: div,
      htmlAttributes: { classList: 'modal-crop' },
    });

    this.modal.addEventListener(
      'ionModalWillPresent',
      this.modalWillPresent.bind(this)
    );
    this.modal.addEventListener(
      'ionModalDidDismiss',
      this.modalDidDismiss.bind(this)
    );

    this.modalPresent();
  }

  modalPresent() {
    this.modal.present();
  }

  modalWillPresent() {
    this.cropperInit();
  }

  modalDismiss() {
    this.modal.dismiss();
  }

  modalDidDismiss() {
    this.cropper.destroy();
  }

  chooseImg() {
    this.input.click();
  }

  async submitImg() {
    const data = {
      cropper: {
        picture: this.imageDataURL,
        picture_file_name: this.fileName,
      },
    };

    try {
      await this.commonAction.post(this.imgSubmitUrl, {
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      this.croppedImgSrc = data.cropper.picture;
      this.modalDismiss();
      this.dispatchEvent(
        new Event('uploadContainerSubmitImg-success', {
          bubbles: true,
          composed: true,
        })
      );
    } catch {
      this.dispatchEvent(
        new Event('uploadContainerSubmitImg-error', {
          bubbles: true,
          composed: true,
        })
      );
      this.snackDanger();
    }
  }

  async deleteImg() {
    if (
      !this.commonAction.confirmDialog(this.t('confirm_dialog_msg.default'))
    ) {
      return false;
    }

    try {
      await this.commonAction.delete(this.imgDeleteUrl);
      this.croppedImgSrc = '';
      this.dispatchEvent(
        new Event('uploadContainerDeleteImg-success', {
          bubbles: true,
          composed: true,
        })
      );
    } catch {
      this.snackDanger();
    }
  }

  renderLabelCaption() {
    return html`
      <ef-icon name=${this.config.icons.label}></ef-icon>
      <div class="info-primary">${this.t('placeholder')}</div>
      <div class="info-secondary">
        ${this.t('recommended_size')}
        <span>${this.recommendedSizeStr}</span>
      </div>
    `;
  }

  renderActions() {
    return html`
      <ef-grid-list alignV="center" col="0" class="actions-group">
        <slot name="action-replace" @click=${this.chooseImg}></slot>
        <slot name="action-remove" @click=${this.deleteImg}></slot>
      </ef-grid-list>
    `;
  }

  render() {
    return html`
      <div
        class="upload-container"
        style="max-width: ${this.imgContainerWidth}px">
        <input
          @drop=${this.handleInputDrop}
          @change=${this.handleInputChange}
          @dragover=${this.handleInputDragOver}
          @dragleave=${this.handleDragLeave}
          class=${classMap({ active: this.dragOver })}
          type="file"
          name="file"
          accept="image/*"
          aria-label="upload" />
        <div
          class="input-label"
          style="background-image: url('${this.croppedImgSrc}')">
          ${this.croppedImgSrc ? null : this.renderLabelCaption()}
        </div>
      </div>
      ${this.croppedImgSrc ? this.renderActions() : null}
    `;
  }
}

UploadContainer.registerTagName('ef-upload-container');

customElements.get('ef-upload-container') ||
  customElements.define('ef-upload-container', UploadContainer);
